import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { LegacyOpsBanner } from "../components/LegacyOpsBanner";
import { AuthRequired } from "../components/AuthRequired";
import { WithdrawForm } from "../components/WithdrawForm";
import { MoneyFlowStepper, type MoneyFlowStep } from "../components/MoneyFlowStepper";
import { EmptyState } from "../components/EmptyState";
import { apiCall, ApiHttpError, RouteNotFoundError } from "../api/client";
import {
  ApprovalRequiredChip,
  GAOSAFE_APPROVAL_REASON,
  useWritesBlocked,
} from "../authority";

// Withdraw treasury page — full lifecycle, browser-driven.
//
// Three sections, top → bottom:
//   1. Availability — GET /v2/admin/bff/escrow/treasury-withdrawable
//      Shows the BE-canonical chain / escrow / token / treasury
//      addresses + live on-chain `treasuryWithdrawable` + the
//      `canCreateProposal` boolean with its `reason` (when false).
//   2. Create proposal — POST /v2/admin/bff/escrow/withdraw-treasury/proposals
//      Amount defaults to the full withdrawable; operator can edit
//      but can't exceed (validation is enforced server-side too).
//      ttlSec defaults to 28800 (8 hours). On success, link the
//      operator to /approvals/<id> for the multisign ceremony.
//   3. Execute — POST /v2/admin/bff/escrow/withdraw-treasury
//      The pre-existing WithdrawForm, still in place. Requires a
//      proposalId of an `approved` proposal. The BE payload-match
//      guard (PR #138) rejects token/amount drift between request
//      and proposal.

interface TreasuryWithdrawableResponse {
  ok: boolean;
  chainId: number;
  network: string;
  escrow: string;
  token: string;
  tokenSymbol: string;
  tokenDecimals: number;
  treasury: string;
  withdrawableBaseUnits: string;
  withdrawableHumanReadable: string;
  paused: boolean | null;
  killSwitchTripped: boolean;
  killSwitchReason: string | null;
  canCreateProposal: boolean;
  reason: string | null;
}

interface CreateProposalResponse {
  ok: boolean;
  proposalId: string;
  actionDigest: string;
  payloadHash: string;
  proposal: {
    id: string;
    thresholdRequired: number;
    status: string;
    expiresAt: string;
  };
}

function AvailabilityCard({
  data,
  loading,
  error,
  onRefresh,
}: {
  data: TreasuryWithdrawableResponse | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  return (
    <section
      style={{
        margin: "12px 0",
        padding: 14,
        border: "1px solid var(--border, #ccc)",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <h4 style={{ fontSize: 13, margin: "0 0 8px 0" }}>Treasury availability</h4>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          style={{ marginLeft: "auto", padding: "2px 10px", fontSize: 12 }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      <details style={{ margin: "4px 0 8px 0" }}>
        <summary style={{ fontSize: 11, color: "var(--fg-dim)", cursor: "pointer" }}>
          Advanced — endpoint
        </summary>
        <p style={{ margin: "4px 0", fontSize: 11, color: "var(--fg-dim)" }}>
          Bound to: <code>GET /v2/admin/bff/escrow/treasury-withdrawable</code>
        </p>
      </details>

      {error && (
        <p style={{ color: "#d23f3f", fontSize: 12 }}>{error}</p>
      )}

      {data && (
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td style={{ padding: "2px 6px", color: "var(--fg-dim)" }}>Chain</td>
              <td style={{ padding: "2px 6px" }}>
                <strong>{data.network}</strong> (chain id {data.chainId})
              </td>
            </tr>
            <tr>
              <td style={{ padding: "2px 6px", color: "var(--fg-dim)" }}>Escrow</td>
              <td style={{ padding: "2px 6px", fontFamily: "monospace", fontSize: 11 }}>
                {data.escrow}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "2px 6px", color: "var(--fg-dim)" }}>Token</td>
              <td style={{ padding: "2px 6px", fontFamily: "monospace", fontSize: 11 }}>
                {data.token} <strong style={{ fontFamily: "inherit" }}>{data.tokenSymbol}</strong>{" "}
                (decimals {data.tokenDecimals})
              </td>
            </tr>
            <tr>
              <td style={{ padding: "2px 6px", color: "var(--fg-dim)" }}>Destination</td>
              <td style={{ padding: "2px 6px", fontFamily: "monospace", fontSize: 11 }}>
                {data.treasury}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "2px 6px", color: "var(--fg-dim)" }}>Withdrawable</td>
              <td style={{ padding: "2px 6px" }}>
                <strong>{data.withdrawableHumanReadable}</strong>{" "}
                <span style={{ color: "var(--fg-dim)", fontSize: 11 }}>
                  ({data.withdrawableBaseUnits} base units)
                </span>
              </td>
            </tr>
            <tr>
              <td style={{ padding: "2px 6px", color: "var(--fg-dim)" }}>paused()</td>
              <td style={{ padding: "2px 6px" }}>
                {data.paused === null ? (
                  <span style={{ color: "var(--fg-dim)" }}>unknown (RPC)</span>
                ) : data.paused ? (
                  <span style={{ color: "#d23f3f" }}>true</span>
                ) : (
                  <span style={{ color: "#0a9444" }}>false</span>
                )}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "2px 6px", color: "var(--fg-dim)" }}>H-8 kill-switch</td>
              <td style={{ padding: "2px 6px" }}>
                {data.killSwitchTripped ? (
                  <span style={{ color: "#d23f3f" }}>
                    TRIPPED ({data.killSwitchReason ?? "unknown"})
                  </span>
                ) : (
                  <span style={{ color: "#0a9444" }}>untripped</span>
                )}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "2px 6px", color: "var(--fg-dim)" }}>Can create proposal</td>
              <td style={{ padding: "2px 6px" }}>
                {data.canCreateProposal ? (
                  <span style={{ color: "#0a9444" }}>yes</span>
                ) : (
                  <span style={{ color: "#d23f3f" }}>no ({data.reason})</span>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </section>
  );
}

function CreateProposalForm({
  availability,
  onCreated,
}: {
  availability: TreasuryWithdrawableResponse;
  onCreated: (proposalId: string) => void;
}) {
  const [amount, setAmount] = useState(availability.withdrawableBaseUnits);
  // Hook — declared with the other hooks, above any early return.
  const writesBlocked = useWritesBlocked();
  const [ttlSecStr, setTtlSecStr] = useState("28800");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateProposalResponse | null>(null);

  // Sync the amount input default whenever availability is refreshed.
  useEffect(() => {
    setAmount(availability.withdrawableBaseUnits);
  }, [availability.withdrawableBaseUnits]);

  const amountValid = /^(?:0|[1-9][0-9]*)$/.test(amount) && amount !== "0";
  const amountWithinBudget = (() => {
    if (!amountValid) return false;
    try {
      return BigInt(amount) <= BigInt(availability.withdrawableBaseUnits);
    } catch {
      return false;
    }
  })();
  const ttlValid =
    /^[1-9][0-9]*$/.test(ttlSecStr) && Number(ttlSecStr) >= 60;

  const canSubmit =
    !writesBlocked &&
    availability.canCreateProposal &&
    amountValid &&
    amountWithinBudget &&
    ttlValid &&
    !busy;

  async function submit() {
    setError(null);
    setBusy(true);
    try {
      const j = await apiCall<CreateProposalResponse>(
        "/v2/admin/bff/escrow/withdraw-treasury/proposals",
        {
          method: "POST",
          body: { amount, ttlSec: Number(ttlSecStr) },
        },
      );
      setResult(j);
      onCreated(j.proposalId);
    } catch (e) {
      if (e instanceof RouteNotFoundError) {
        setError("Route not registered on BE. Check deploy.");
      } else if (e instanceof ApiHttpError) {
        const bodyMsg =
          e.body !== null && typeof e.body === "object" && "message" in e.body
            ? String((e.body as { message: unknown }).message)
            : "";
        const reason =
          e.body !== null && typeof e.body === "object" && "reason" in e.body
            ? String((e.body as { reason: unknown }).reason)
            : "";
        setError(
          `HTTP ${e.status}${bodyMsg ? ` — ${bodyMsg}` : ""}${reason ? ` [${reason}]` : ""}`,
        );
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError("Unknown error.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      style={{
        margin: "12px 0",
        padding: 14,
        border: "1px solid var(--border, #ccc)",
      }}
    >
      <h4 style={{ fontSize: 13, margin: "0 0 8px 0" }}>Create multisign withdraw proposal</h4>
      <details style={{ margin: "4px 0 8px 0" }}>
        <summary style={{ fontSize: 11, color: "var(--fg-dim)", cursor: "pointer" }}>
          Advanced — endpoint
        </summary>
        <p style={{ margin: "4px 0", fontSize: 11, color: "var(--fg-dim)" }}>
          Bound to: <code>POST /v2/admin/bff/escrow/withdraw-treasury/proposals</code>. BE canonicalises
          escrow / token / destination / chain. Browser controls only amount + ttlSec.
        </p>
      </details>

      <div style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "8px 12px", margin: "8px 0", fontSize: 12 }}>
        <label htmlFor="amt-input" style={{ alignSelf: "center" }}>
          Amount (base units)
        </label>
        <div>
          <input
            id="amt-input"
            type="text"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={!availability.canCreateProposal || busy}
            style={{
              width: "100%",
              padding: "4px 8px",
              fontFamily: "monospace",
              fontSize: 12,
            }}
          />
          <div style={{ fontSize: 11, color: "var(--fg-dim)", marginTop: 2 }}>
            Max: {availability.withdrawableBaseUnits} (
            {availability.withdrawableHumanReadable}).{" "}
            {!amountValid && (
              <span style={{ color: "#d23f3f" }}>Must be a positive integer.</span>
            )}
            {amountValid && !amountWithinBudget && (
              <span style={{ color: "#d23f3f" }}> Exceeds withdrawable.</span>
            )}
          </div>
        </div>

        <label htmlFor="ttl-input" style={{ alignSelf: "center" }}>
          TTL (seconds)
        </label>
        <div>
          <input
            id="ttl-input"
            type="text"
            value={ttlSecStr}
            onChange={(e) => setTtlSecStr(e.target.value)}
            disabled={!availability.canCreateProposal || busy}
            style={{
              width: 160,
              padding: "4px 8px",
              fontFamily: "monospace",
              fontSize: 12,
            }}
          />
          <span style={{ fontSize: 11, color: "var(--fg-dim)", marginLeft: 8 }}>
            (default 28800 = 8h; max 86400 = 24h; min 60)
            {!ttlValid && (
              <span style={{ color: "#d23f3f" }}> Must be integer ≥ 60.</span>
            )}
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={writesBlocked ? undefined : () => void submit()}
        title={writesBlocked ? GAOSAFE_APPROVAL_REASON : undefined}
        disabled={!canSubmit}
        style={{
          marginTop: 8,
          padding: "6px 14px",
          fontSize: 13,
          background: "var(--accent, #2c7be5)",
          color: "white",
          border: "none",
          borderRadius: 3,
          cursor: canSubmit ? "pointer" : "default",
          opacity: canSubmit ? 1 : 0.5,
        }}
      >
        {busy ? "Creating…" : "Create multisign withdraw proposal"}
      </button>
      {writesBlocked && (
        <span style={{ marginLeft: 10 }}>
          <ApprovalRequiredChip />
        </span>
      )}

      {!availability.canCreateProposal && (
        <p style={{ marginTop: 8, fontSize: 12, color: "var(--fg-dim)" }}>
          Cannot create proposal: <code>{availability.reason}</code>. Address that first.
        </p>
      )}

      {error && (
        <p role="alert" style={{ marginTop: 8, fontSize: 12, color: "#d23f3f" }}>
          {error}
        </p>
      )}

      {result && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            border: "1px solid #0a9444",
            background: "rgba(10, 148, 68, 0.06)",
            fontSize: 12,
          }}
        >
          <p style={{ margin: 0, color: "#0a9444", fontWeight: 600 }}>
            ✓ Proposal created
          </p>
          <p style={{ margin: "4px 0", wordBreak: "break-all" }}>
            ID: <code>{result.proposalId}</code>
          </p>
          <p style={{ margin: "4px 0" }}>
            Threshold: {result.proposal.thresholdRequired} · Status:{" "}
            <strong>{result.proposal.status}</strong> · Expires:{" "}
            <code>{result.proposal.expiresAt}</code>
          </p>
          <p style={{ margin: "8px 0 0 0" }}>
            <Link to={`/approvals/${result.proposalId}`} style={{ color: "var(--accent, #2c7be5)" }}>
              Open multisign approval flow →
            </Link>
          </p>
        </div>
      )}
    </section>
  );
}

function ExecuteSection({
  availability,
  lastCreatedProposalId,
}: {
  availability: TreasuryWithdrawableResponse;
  lastCreatedProposalId: string | null;
}) {
  return (
    <section
      style={{
        margin: "12px 0",
        padding: 14,
        border: "1px solid var(--border, #ccc)",
        background: "rgba(210, 63, 63, 0.04)",
      }}
    >
      <h4 style={{ fontSize: 13, margin: "0 0 8px 0" }}>Execute approved proposal</h4>
      <p style={{ margin: "4px 0", fontSize: 12, color: "#d23f3f" }}>
        ⚠ This broadcasts an on-chain transaction. Only call this AFTER the proposal has reached
        status=approved (2-of-2 owner signatures via the Approvals tab).
      </p>
      <details style={{ margin: "4px 0" }}>
        <summary style={{ fontSize: 11, color: "var(--fg-dim)", cursor: "pointer" }}>
          Advanced — endpoint
        </summary>
        <p style={{ margin: "4px 0", fontSize: 11, color: "var(--fg-dim)" }}>
          Bound to: <code>POST /v2/admin/bff/escrow/withdraw-treasury</code>. The BE payload-match
          guard refuses if request token + amount don't match the approved proposal byte-for-byte.
        </p>
      </details>

      {lastCreatedProposalId && (
        <p style={{ margin: "4px 0", fontSize: 12 }}>
          Last created proposal:{" "}
          <Link to={`/approvals/${lastCreatedProposalId}`} style={{ color: "var(--accent, #2c7be5)" }}>
            <code>{lastCreatedProposalId}</code>
          </Link>{" "}
          — paste this id (and the matching token / amount) below once 2/2 owners have signed.
        </p>
      )}

      <p style={{ margin: "4px 0 12px 0", fontSize: 11, color: "var(--fg-dim)" }}>
        Suggested values for sweep proposals:
        <br />
        token: <code>{availability.token}</code>
        <br />
        amount (full): <code>{availability.withdrawableBaseUnits}</code>
      </p>

      <WithdrawForm
        showAffiliate={false}
        path="/v2/admin/bff/escrow/withdraw-treasury"
        buttonLabel="POST withdraw-treasury (broadcasts on-chain)"
        description="Drains the treasury bucket from the V3 escrow to the BE-configured destination. Single execution — duplicate calls hit the already_executed guard."
      />
    </section>
  );
}

function WithdrawTreasuryInner() {
  const [availability, setAvailability] = useState<TreasuryWithdrawableResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastCreatedProposalId, setLastCreatedProposalId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const j = await apiCall<TreasuryWithdrawableResponse>(
        "/v2/admin/bff/escrow/treasury-withdrawable",
      );
      setAvailability(j);
    } catch (e) {
      if (e instanceof RouteNotFoundError) {
        setError(
          "Route /v2/admin/bff/escrow/treasury-withdrawable not registered. Check the BE deploy.",
        );
      } else if (e instanceof ApiHttpError) {
        setError(`HTTP ${e.status} from /v2/admin/bff/escrow/treasury-withdrawable.`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError("Unknown error.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const steps: ReadonlyArray<MoneyFlowStep> = [
    {
      label: "Review availability",
      hint: "Confirm chain / escrow / token / treasury + withdrawable.",
      state: availability ? "done" : "current",
    },
    {
      label: "Create treasury proposal",
      hint: "Operator picks amount; BE canonicalises the rest.",
      state: !availability ? "upcoming" : lastCreatedProposalId ? "done" : "current",
    },
    {
      label: "Approve proposal (2/2)",
      hint: "Collect owner signatures in Approvals.",
      state: lastCreatedProposalId ? "current" : "upcoming",
    },
    {
      label: "Execute withdraw",
      hint: "Broadcasts on-chain after the proposal is approved.",
      state: lastCreatedProposalId ? "current" : "upcoming",
    },
  ];

  return (
    <div>
      <div
        role="alert"
        style={{
          margin: "8px 0",
          padding: "10px 12px",
          border: "1px solid var(--danger, #d23f3f)",
          background: "rgba(210, 63, 63, 0.06)",
          color: "var(--danger, #d23f3f)",
          borderRadius: 4,
          fontSize: 12.5,
          fontWeight: 600,
        }}
      >
        Treasury withdrawal sends USDC to the treasury wallet, not an affiliate
        user. To pay a partner, use{" "}
        <Link to="/admin/affiliate/withdrawals" style={{ color: "inherit", textDecoration: "underline" }}>
          Affiliate Payouts
        </Link>{" "}
        instead.
      </div>

      <p style={{ margin: "4px 0", fontSize: 12, color: "var(--fg-dim)" }}>
        Full lifecycle: <strong>view → create proposal → approve 2/2 → execute → history</strong>.
        No HMAC in browser. Session+device gated. Owner identity + wallet derived from session.{" "}
        <Link to="/approvals/history" style={{ color: "var(--accent, #2c7be5)" }}>
          History →
        </Link>
      </p>

      <MoneyFlowStepper steps={steps} ariaLabel="Treasury withdrawal steps" />

      <AvailabilityCard data={availability} loading={loading} error={error} onRefresh={load} />

      {availability && availability.withdrawableBaseUnits === "0" && (
        <EmptyState
          title="No treasury funds available."
          hint="There is nothing to withdraw right now — creating a proposal is disabled until the treasury has a positive balance."
        />
      )}

      {availability && availability.withdrawableBaseUnits !== "0" && (
        <CreateProposalForm
          availability={availability}
          onCreated={(id) => setLastCreatedProposalId(id)}
        />
      )}

      {availability && availability.withdrawableBaseUnits !== "0" && (
        <ExecuteSection
          availability={availability}
          lastCreatedProposalId={lastCreatedProposalId}
        />
      )}
    </div>
  );
}

export function WithdrawTreasury() {
  return (
    <div>
      <PageHeader
        title="Withdraw treasury"
        subtitle="Full lifecycle: view availability, create multisign proposal, approve 2/2, execute. No HMAC in browser."
        method="GET / POST"
        path="/v2/admin/bff/escrow/treasury-withdrawable + /withdraw-treasury/proposals + /withdraw-treasury"
      />
      <LegacyOpsBanner what="This is the legacy escrow treasury withdraw." />
      <AuthRequired>
        <WithdrawTreasuryInner />
      </AuthRequired>
    </div>
  );
}
